import React, { useEffect, useState } from 'react';
import styles from './DropdownMenu.module.scss';
import LoaderButton from '../../LoaderButton/LoaderButton';
import { CHEVRON_DOWN, CLOSE, SEARCH } from '@/src/lib/SmartDB/Commons/Constants/images';

interface IDropdownMenuProps<T> {
    name: string;
    namePlural?: string;
    isLoadingItems?: boolean;
    styles?: typeof styles;
    className?: string;
    items: T[];
    selected: T | undefined;
    setSelected: (selected: T) => void;
    itemReact: (item: T | undefined) => React.ReactNode;
    itemReactUnSelected?: () => React.ReactNode;
    itemReactEmpty?: () => React.ReactNode;
    swShowTitleInSelected?: boolean;
    swCloseAutomatically?: boolean;
}

const DropdownMenu = <T extends any>(props: IDropdownMenuProps<T>) => {
    //--------------------------------------
    const {
        name,
        isLoadingItems,
        items,
        selected,
        setSelected,
        itemReactEmpty,
        itemReactUnSelected,
        itemReact,
        swShowTitleInSelected,
        className,
        swCloseAutomatically: swCloseAuto,
    } = props;
    //--------------------------------------
    //TODO: hacer algo para evitar que seleccione una swap offer mientras esta loading
    // useEffect(() => {
    //     if (isLoadingItems === true) setIsOpen(false);
    // }, [isLoadingItems]);
    //--------------------------------------
    const mainStyle = props.styles !== undefined ? (className !== undefined ? props.styles[className] : props.styles.filterStyle) : styles.filterStyle;
    const namePlural = props.namePlural !== undefined ? props.namePlural : `${name}`;
    //--------------------------------------
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const handleClickOpen = () => {
        isOpen ? setIsOpen(false) : items.length > 0 ? setIsOpen(true) : null;
    };
    //--------------------------------------
    const [closeTimeoutId, setCloseTimeoutId] = useState<NodeJS.Timeout | null>(null);
    //--------------------------------------
    const handleMouseEnter = () => {
        // If there's a pending timeout to close the dropdown, clear it
        if (swCloseAuto !== false && closeTimeoutId) {
            clearTimeout(closeTimeoutId);
            setCloseTimeoutId(null);
        }
        // setIsOpen(true);
    };
    //--------------------------------------
    const handleMouseLeave = () => {
        // Set a timeout to close the dropdown after a few seconds
        if (swCloseAuto !== false) {
            const timeoutId = setTimeout(() => {
                setIsOpen(false);
            }, 200); // Delay in milliseconds (1000ms = 1 second)
            setCloseTimeoutId(timeoutId);
        }
    };
    //--------------------------------------
    return (
        <div className={mainStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div className={`${props.styles?.filterGroup ?? styles.filterGroup} ${items.length > 0 ? props.styles?.available ?? styles.available : ''}`} onClick={handleClickOpen}>
                <div className={props.styles?.filterItem ?? styles.filterItem}>
                    {isLoadingItems === true ? (
                        <>
                            <p className={props.styles?.filterTitle ?? styles.filterTitle}>{name}</p>
                            <div className={props.styles?.filterValue ?? styles.filterValue}>
                                <div className={props.styles?.loading ?? styles.loading}>
                                    <LoaderButton />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {items === undefined || items.length === 0 ? (
                                <>
                                    <div className={props.styles?.filterTitle ?? styles.filterTitle}>{name}</div>
                                    <div className={props.styles?.filterValue ?? styles.filterValue}>{itemReactEmpty?.() ?? `No available ${namePlural}`}</div>
                                </>
                            ) : selected === undefined ? (
                                <>
                                    <div className={props.styles?.filterTitle ?? styles.filterTitle}>{name}</div>
                                    <div className={props.styles?.filterValue ?? styles.filterValue}>{itemReactUnSelected?.() ?? `Select ${name}`}</div>
                                </>
                            ) : (
                                <>
                                    {swShowTitleInSelected === true && <div className={props.styles?.filterTitle ?? styles.filterTitle}>{name}</div>}
                                    <div className={props.styles?.filterValue ?? styles.filterValue}>{itemReact(selected)}</div>
                                </>
                            )}
                        </>
                    )}
                </div>
                <svg width="20" height="20" id="icon" className={`${props.styles?.icon ?? styles.icon} ${isOpen ? props.styles?.open ?? styles.open : ''}`}>
                    <use href={CHEVRON_DOWN}></use>
                </svg>
            </div>
            {isOpen && items.length > 0 && (
                <div className={props.styles?.filterOptions ?? styles.filterOptions}>
                    {items.map((value, index) => (
                        <div
                            key={index}
                            className={`${props.styles?.item ?? styles.item} ${selected === value ? props.styles?.active ?? styles.active : ''}`}
                            onClick={() => {
                                if (isLoadingItems === true) return;
                                setSelected(value);
                                setIsOpen(false);
                            }}
                        >
                            <>{itemReact(value)}</>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DropdownMenu;
